import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { HttpClient } from '@angular/common/http';
import { Subject, of } from 'rxjs';
import {
  takeUntil,
  catchError,
  take,
  filter,
  timeout,
  finalize,
} from 'rxjs/operators';
import { environment } from '../../../environment/environment';
import { UserService } from '../../../services/user/user.service';
import { AuthService } from '../../../services/auth/auth.service';
import { ActivatedRoute } from '@angular/router';
import {
  DUNA_EVENT_NOTIFICATION_PREFERENCE_ROWS,
  NotificationPreferencesMatrix,
  normalizeNotificationPreferencesMatrix,
} from '../../../constants/duna-notification-preference-types';

interface NotificationPreferencesResponse {
  notification_preferences?: NotificationPreferencesMatrix;
  effective?: NotificationPreferencesMatrix;
  customized?: boolean;
}

@Component({
  selector: 'app-duna-user-notification-preferences',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './duna-user-notification-preferences.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duna-user-notification-preferences.component.scss'],
})
export class DunaUserNotificationPreferencesComponent implements OnInit, OnDestroy {
  readonly rows = DUNA_EVENT_NOTIFICATION_PREFERENCE_ROWS;

  targetUuid = '';
  preferences: NotificationPreferencesMatrix =
    normalizeNotificationPreferencesMatrix(null);
  loading = true;
  saving = false;
  errorMessage: string | null = null;
  accessDenied = false;

  private destroy$ = new Subject<void>();

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private authService: AuthService,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  private getApiBaseUrl(): string {
    let base = (environment.apiUrl || '').replace(/\/$/, '');
    if (base && !/^https?:\/\//i.test(base)) {
      base = 'https://' + base;
    }
    return base;
  }

  ngOnInit(): void {
    this.authService.authStateReady$
      .pipe(
        filter((ready) => ready),
        take(1),
        timeout(15000),
        takeUntil(this.destroy$)
      )
      .subscribe({
        next: () => this.resolveTargetAndLoad(),
        error: () => this.resolveTargetAndLoad(),
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private resolveTargetUuid(): string | null {
    const fromUrl = this.userService.getCurrentUserUuid();
    if (fromUrl) {
      return fromUrl;
    }
    const queryUuid = this.route.snapshot.queryParamMap.get('user');
    if (queryUuid) {
      return queryUuid;
    }
    return this.authService.getCurrentUser()?.uuid ?? null;
  }

  private resolveTargetAndLoad(): void {
    const uuid = this.resolveTargetUuid();
    if (!uuid) {
      this.loading = false;
      this.errorMessage = 'Kunne ikke finde bruger.';
      this.cdr.markForCheck();
      return;
    }

    this.targetUuid = uuid;
    const base = this.getApiBaseUrl();
    if (!base) {
      this.loading = false;
      this.errorMessage = 'API-adresse mangler.';
      this.cdr.markForCheck();
      return;
    }

    this.http
      .get<NotificationPreferencesResponse>(
        `${base}/users/${uuid}/notification-preferences`,
        { withCredentials: true }
      )
      .pipe(
        timeout(20000),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.preferences = normalizeNotificationPreferencesMatrix(
            res?.notification_preferences ?? res?.effective
          );
        },
        error: (err) => {
          if (err?.status === 403) {
            this.accessDenied = true;
            this.errorMessage = null;
          } else {
            this.errorMessage = 'Kunne ikke hente notifikationsindstillinger.';
          }
        },
      });
  }

  isEmailChecked(key: string): boolean {
    return !!this.preferences[key]?.email;
  }

  isPushChecked(key: string): boolean {
    return !!this.preferences[key]?.push;
  }

  setEmail(key: string, checked: boolean): void {
    const row = this.preferences[key] ?? { email: true, push: true };
    this.preferences = {
      ...this.preferences,
      [key]: { ...row, email: checked },
    };
  }

  setPush(key: string, checked: boolean): void {
    const row = this.preferences[key] ?? { email: true, push: true };
    this.preferences = {
      ...this.preferences,
      [key]: { ...row, push: checked },
    };
  }

  save(): void {
    if (!this.targetUuid || this.saving) {
      return;
    }
    const base = this.getApiBaseUrl();
    if (!base) {
      return;
    }

    this.saving = true;
    this.http
      .put<NotificationPreferencesResponse>(
        `${base}/users/${this.targetUuid}/notification-preferences`,
        { notification_preferences: this.preferences },
        { withCredentials: true }
      )
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res) => {
          this.preferences = normalizeNotificationPreferencesMatrix(
            res?.notification_preferences ?? this.preferences
          );
          this.snackBar.open('Notifikationer gemt', undefined, {
            duration: 2500,
          });
        },
        error: () => {
          this.snackBar.open('Kunne ikke gemme notifikationer', undefined, {
            duration: 3500,
          });
        },
      });
  }
}
